import React, { useMemo, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../../../redux/store";
import useActiveTab from "../../../../hooks/useActiveTab";
import { hasAccess } from "../../../../access";
import { TabOptions } from "../../../../types";
import BasicPageLayout from "../../../../components/BasicPageLayout";
import {
	SideTab,
	StyledRow,
	SuspenseLoader,
} from "../../../../components/common";

import { TabKeys, TabAccessNames } from "./constants/access";

const LazyProvider = lazy(async () => {
	const elem = await import(
		"./tabs/Provider" /* webpackChunkName: "settings-receipt-provider-page" */
	);
	return { default: elem.default };
});

const ReceiptProviderSettings: React.FC = () => {
	const { t } = useTranslation();

	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	const tabs: TabOptions.Array = useMemo(
		() =>
			[
				{
					key: TabKeys.PROVIDER,
					label: t("settings.receiptProviders.provider.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyProvider />
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.PROVIDER],
				},
			].filter((btn) => hasAccess(btn.accessName, personalRole)),
		[t, personalRole],
	);

	const { activeKey, activeTab, setActiveKey } = useActiveTab(tabs);

	return (
		<BasicPageLayout
			navigation={
				<SideTab
					value={activeKey}
					onChange={setActiveKey}
					options={tabs}
					variant="vertical"
				/>
			}
			content={activeTab?.value?.render?.() ?? null}
		/>
	);
};

export default ReceiptProviderSettings;
